import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, Copy, Clock } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { format, differenceInDays } from "date-fns";
import { useSupabaseRealtimeInvalidation } from "@/hooks/useSupabaseRealtimeInvalidation";
import { useAuth } from "@/hooks/useAuth";
import {
  resolveFundCbam,
  type FeBreakdownRow,
  type FeMappingRow,
  type FeSplitOverride,
} from "@/components/fe/feMatching";


interface CbamFund {
  id: string;
  fund_manager: string | null;
  fund_name: string;
  fund_class: string | null;
  isin: string | null;
}

interface SectorMapping {
  id: string;
  fund_id: string;
  clarification_number: number;
  sector_id: string;
  weighting: number;
}

interface MasterSector {
  id: string;
  name: string;
}

interface FundReview {
  fund_id: string;
  last_reviewed_at: string;
}

interface FundWeight {
  fund_id: string;
  weight_pct: number;
}

interface SectorWeightingsProps {
  side?: "p" | "r" | "dist" | "mm";
}

const SLOTS = [1, 2, 3, 4, 5];

export default function SectorWeightings({ side = "p" }: SectorWeightingsProps) {
  const queryClient = useQueryClient();
  const { loading: authLoading, session } = useAuth();
  const authReady = !authLoading && Boolean(session);
  const weightsTable = side === "r"
    ? "ba_r_cbam_fund_weights"
    : side === "dist"
    ? "ba_dist_cbam_fund_weights"
    : side === "mm"
    ? "ba_mm_cbam_fund_weights"
    : "ba_cbam_fund_weights";
  const fundsQueryKey = ["ba-cbam-funds-all", "manual-sector-weightings", side];
  const mappingsQueryKey = ["ba-fund-sector-mappings", "manual-sector-weightings", side];
  const sectorsQueryKey = ["ba-master-sectors", "manual-sector-weightings", side];
  const reviewsQueryKey = ["ba-fund-sector-reviews", "manual-sector-weightings", side];
  const heldFundQueryKey = [`ba-${side}-held-fund-ids`];

  useSupabaseRealtimeInvalidation(
    `sector-weightings-${side}`,
    [
      "ba_cbam_funds",
      "ba_cbam_fund_sector_mappings",
      "ba_master_sectors",
      "ba_cbam_fund_sector_reviews",
      weightsTable,
    ],
    [
      fundsQueryKey,
      mappingsQueryKey,
      sectorsQueryKey,
      reviewsQueryKey,
      heldFundQueryKey,
    ],
  );

  const { data: funds, isLoading: fundsLoading } = useQuery({
    queryKey: fundsQueryKey,
    enabled: authReady,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ba_cbam_funds")
        .select("id, fund_manager, fund_name, fund_class, isin")
        .order("fund_name");
      if (error) throw error;
      return data as CbamFund[];
    },
  });

  const { data: feRows } = useQuery({
    queryKey: ["fe-asset-breakdown-latest"],
    enabled: authReady,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("fe_asset_breakdown")
        .select("id, isin, fund_name, as_of_date, breakdown")
        .order("as_of_date", { ascending: false });
      if (error) throw error;
      const latest = new Map<string, FeBreakdownRow>();
      for (const r of (data ?? []) as any[]) {
        if (!latest.has(r.isin)) latest.set(r.isin, { ...r, breakdown: r.breakdown ?? {} });
      }
      return Array.from(latest.values());
    },
  });

  const { data: feMapping } = useQuery({
    queryKey: ["fe-sector-mapping"],
    enabled: authReady,
    queryFn: async () => {
      const { data, error } = await supabase.from("fe_sector_mapping").select("*").order("display_order");
      if (error) throw error;
      return data as FeMappingRow[];
    },
  });

  const { data: feOverrides } = useQuery({
    queryKey: ["fe-split-overrides"],
    enabled: authReady,
    queryFn: async () => {
      const { data, error } = await supabase.from("fe_fund_split_overrides").select("*");
      if (error) throw error;
      return data as FeSplitOverride[];
    },
  });

  const { data: mappings, isLoading: mappingsLoading } = useQuery({
    queryKey: mappingsQueryKey,
    enabled: authReady,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ba_cbam_fund_sector_mappings")
        .select("*")
        .order("clarification_number");
      if (error) throw error;
      return data as SectorMapping[];
    },
  });

  const { data: sectors, isLoading: sectorsLoading } = useQuery({
    queryKey: sectorsQueryKey,
    enabled: authReady,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ba_master_sectors")
        .select("id, name")
        .order("display_order");
      if (error) throw error;
      return data as MasterSector[];
    },
  });

  const { data: reviews, isLoading: reviewsLoading } = useQuery({
    queryKey: reviewsQueryKey,
    enabled: authReady,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ba_cbam_fund_sector_reviews")
        .select("fund_id, last_reviewed_at");
      if (error) throw error;
      return data as FundReview[];
    },
  });

  const { data: activeWeights, isLoading: weightsLoading } = useQuery({
    queryKey: heldFundQueryKey,
    enabled: authReady,
    queryFn: async () => {
      const { data, error } = await supabase
        .from(weightsTable)
        .select("fund_id, portfolio, weight_pct, effective_date")
        .order("effective_date", { ascending: false });
      if (error) throw error;
      // Keep only the latest weight per (fund_id, portfolio); ignore stale older rows.
      const seen = new Set<string>();
      const latest: FundWeight[] = [];
      for (const w of (data ?? []) as any[]) {
        const k = `${w.fund_id}::${w.portfolio}`;
        if (seen.has(k)) continue;
        seen.add(k);
        latest.push({ fund_id: w.fund_id, weight_pct: Number(w.weight_pct) });
      }
      return latest;
    },
  });


  const sectorNameById = new Map((sectors ?? []).map((sector) => [sector.id, sector.name]));
  const hasLoadedRequiredData = Boolean(
    funds && mappings && sectors && reviews && activeWeights && feRows && feMapping && feOverrides
  );
  const heldFundIds = new Set(
    (activeWeights ?? []).filter((w) => Number(w.weight_pct) > 0).map((w) => w.fund_id)
  );

  const visibleFunds = (funds ?? []).filter((fund) => heldFundIds.has(fund.id));

  const allLoading = !authReady || !hasLoadedRequiredData || fundsLoading || mappingsLoading || sectorsLoading || reviewsLoading
    || weightsLoading;

  const markReviewed = async (fund_id: string) => {
    const { data: userData } = await supabase.auth.getUser();
    const { error } = await supabase
      .from("ba_cbam_fund_sector_reviews")
      .upsert(
        { fund_id, last_reviewed_at: new Date().toISOString(), reviewed_by: userData.user?.id, updated_at: new Date().toISOString() },
        { onConflict: "fund_id" }
      );
    if (error) throw error;
  };

  const updateMappingMutation = useMutation({
    mutationFn: async ({ fund_id, clarification_number, sector_id, weighting }: { fund_id: string; clarification_number: number; sector_id: string; weighting: number }) => {
      const { error } = await supabase
        .from("ba_cbam_fund_sector_mappings")
        .upsert(
          { fund_id, clarification_number, sector_id, weighting },
          { onConflict: "fund_id,clarification_number" }
        );
      if (error) throw error;
      await markReviewed(fund_id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mappingsQueryKey });
      queryClient.invalidateQueries({ queryKey: reviewsQueryKey });
      queryClient.invalidateQueries({ queryKey: ["ba-fund-sector-mappings"] });
      queryClient.invalidateQueries({ queryKey: ["ba-fund-sector-reviews"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const saveCbamMutation = useMutation({
    mutationFn: async ({ fund_id, rows }: { fund_id: string; rows: { sector_id: string; weighting: number }[] }) => {
      // Clear all 5 slots, then re-insert provided rows in order (max 5).
      const { error: delErr } = await supabase
        .from("ba_cbam_fund_sector_mappings")
        .delete()
        .eq("fund_id", fund_id);
      if (delErr) throw delErr;
      const trimmed = rows.slice(0, SLOTS.length);
      if (trimmed.length > 0) {
        const payload = trimmed.map((r, i) => ({
          fund_id,
          clarification_number: i + 1,
          sector_id: r.sector_id,
          weighting: r.weighting,
        }));
        const { error } = await supabase
          .from("ba_cbam_fund_sector_mappings")
          .insert(payload);
        if (error) throw error;
      }
      await markReviewed(fund_id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mappingsQueryKey });
      queryClient.invalidateQueries({ queryKey: reviewsQueryKey });
      queryClient.invalidateQueries({ queryKey: ["ba-fund-sector-mappings"] });
      queryClient.invalidateQueries({ queryKey: ["ba-fund-sector-reviews"] });
      toast.success("CBAM allocation saved");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const verifyMutation = useMutation({
    mutationFn: async (fund_id: string) => {
      await markReviewed(fund_id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reviewsQueryKey });
      queryClient.invalidateQueries({ queryKey: ["ba-fund-sector-reviews"] });
      toast.success("Marked as reviewed");
    },
    onError: (e: Error) => toast.error(e.message),
  });


  const feMappingByCol = new Map((feMapping ?? []).map((m) => [m.fe_column_name, m]));
  const feOverridesByIsinCol = new Map<string, FeSplitOverride[]>();
  for (const o of feOverrides ?? []) {
    const k = `${o.isin}::${o.fe_column_name}`;
    if (!feOverridesByIsinCol.has(k)) feOverridesByIsinCol.set(k, []);
    feOverridesByIsinCol.get(k)!.push(o);
  }
  const feByIsin = new Map((feRows ?? []).map((r) => [r.isin.toUpperCase().trim(), r]));
  const sectorIdByName = new Map((sectors ?? []).map((s) => [s.name, s.id]));

  const copyFromFeMutation = useMutation({
    mutationFn: async (fund: CbamFund) => {
      const isin = (fund.isin ?? "").toUpperCase().trim();
      if (!isin) throw new Error("Fund has no ISIN");
      const fe = feByIsin.get(isin);
      if (!fe) throw new Error("No FE breakdown for this ISIN");
      const result = resolveFundCbam(fe, feMappingByCol, feOverridesByIsinCol);
      const entries = Object.entries(result.allocation)
        .map(([name, pct]) => ({ name, pct: Number(pct) || 0, sector_id: sectorIdByName.get(name) }))
        .filter((e) => e.pct > 0 && e.sector_id)
        .sort((a, b) => b.pct - a.pct)
        .slice(0, SLOTS.length);
      if (entries.length === 0) throw new Error("FE allocation has no sectors mappable to CBAM Master Sectors");
      const sum = entries.reduce((s, e) => s + e.pct, 0);
      const rows = entries.map((e, i) => ({
        fund_id: fund.id,
        clarification_number: i + 1,
        sector_id: e.sector_id!,
        weighting: e.pct / sum,
      }));
      // Clear other slots so totals match.
      const usedSlots = new Set(rows.map((r) => r.clarification_number));
      const slotsToClear = SLOTS.filter((s) => !usedSlots.has(s));
      if (slotsToClear.length > 0) {
        const { error: delErr } = await supabase
          .from("ba_cbam_fund_sector_mappings")
          .delete()
          .eq("fund_id", fund.id)
          .in("clarification_number", slotsToClear);
        if (delErr) throw delErr;
      }
      const { error } = await supabase
        .from("ba_cbam_fund_sector_mappings")
        .upsert(rows, { onConflict: "fund_id,clarification_number" });
      if (error) throw error;
      await markReviewed(fund.id);
      return entries.length;
    },
    onSuccess: (n) => {
      queryClient.invalidateQueries({ queryKey: mappingsQueryKey });
      queryClient.invalidateQueries({ queryKey: reviewsQueryKey });
      queryClient.invalidateQueries({ queryKey: ["ba-fund-sector-mappings"] });
      queryClient.invalidateQueries({ queryKey: ["ba-fund-sector-reviews"] });
      toast.success(`Copied ${n} sector${n === 1 ? "" : "s"} from FE`);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const getMapping = (fundId: string, slot: number) =>
    mappings?.find((m) => m.fund_id === fundId && m.clarification_number === slot);

  const getReview = (fundId: string) =>
    reviews?.find((r) => r.fund_id === fundId);

  const sumWeights = (fundId: string) =>
    SLOTS.reduce((acc, s) => acc + Number(getMapping(fundId, s)?.weighting || 0), 0);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Holdings Sector Appropriating</h1>
        <p className="text-sm text-muted-foreground">Map each fund to up to 5 master sectors with weightings (must sum to 100%). Use Verify to confirm a fund has been reviewed without changing weightings.</p>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Sector Weightings</CardTitle>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          {allLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left p-3 font-medium text-muted-foreground min-w-[260px]">Fund</th>
                  <th className="text-left p-3 font-medium text-muted-foreground">CBAM Allocation</th>
                  <th className="text-center p-3 font-medium text-muted-foreground min-w-[80px]">Total</th>
                  <th className="text-center p-3 font-medium text-muted-foreground min-w-[120px]">Verify</th>
                </tr>
              </thead>
              <tbody>
                {visibleFunds?.map((fund, idx) => {
                  const total = sumWeights(fund.id) * 100;
                  const ok = Math.abs(total - 100) < 0.05 || total === 0;
                  const review = getReview(fund.id);
                  const last = review?.last_reviewed_at;
                  const days = last ? differenceInDays(new Date(), new Date(last)) : null;
                  const stale = days === null || days > 60;
                  return (
                    <tr key={fund.id} className={cn("border-b align-top", idx % 2 === 0 ? "bg-background" : "bg-muted/20")}>
                      <td className="p-3 text-xs">
                        <div className="font-medium flex items-center gap-1">
                          {stale && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Clock className="h-3 w-3 text-amber-600 cursor-help" />
                                </TooltipTrigger>
                                <TooltipContent>
                                  {last
                                    ? `Last reviewed ${format(new Date(last), "dd/MM/yyyy")} (${days} days ago)`
                                    : "Sector allocation has never been reviewed"}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                          {fund.fund_name}
                          {fund.fund_class ? <span className="text-muted-foreground"> {fund.fund_class}</span> : null}
                        </div>
                        <div className="text-muted-foreground text-[10px]">
                          {fund.isin ?? "no ISIN"} · {fund.fund_manager ?? ""}
                        </div>
                        <div className="mt-1">
                          <EditCbamDialog
                            fundName={fund.fund_name}
                            sectors={sectors ?? []}
                            current={SLOTS.map((slot) => {
                              const m = getMapping(fund.id, slot);
                              return m ? { sector_id: m.sector_id, weighting: Number(m.weighting) * 100 } : null;
                            }).filter(Boolean) as { sector_id: string; weighting: number }[]}
                            onSave={(rows) =>
                              saveCbamMutation.mutateAsync({
                                fund_id: fund.id,
                                rows: rows.map((r) => ({ sector_id: r.sector_id, weighting: r.weighting / 100 })),
                              })
                            }
                          />
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="flex flex-wrap gap-1">
                          {total === 0 ? (
                            <span className="text-xs text-muted-foreground italic">No allocation set</span>
                          ) : (
                            SLOTS.map((slot) => {
                              const m = getMapping(fund.id, slot);
                              if (!m?.sector_id) return null;
                              const name = sectorNameById.get(m.sector_id) ?? "?";
                              const pct = Number(m.weighting) * 100;
                              return (
                                <Badge key={slot} variant="outline" className="text-[10px]">
                                  {name}: {pct.toFixed(1)}%
                                </Badge>
                              );
                            })
                          )}
                        </div>
                      </td>
                      <td className={cn("p-3 text-center text-xs font-medium tabular-nums", ok ? "text-foreground" : "text-destructive")}>
                        {total.toFixed(1)}%
                      </td>
                      <td className="p-3 text-center">
                        <div className="flex flex-col items-center gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs w-full"
                            disabled={verifyMutation.isPending}
                            onClick={() => verifyMutation.mutate(fund.id)}
                          >
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Verify
                          </Button>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-6 text-[10px] w-full px-1"
                                  disabled={
                                    copyFromFeMutation.isPending ||
                                    !fund.isin ||
                                    !feByIsin.has((fund.isin ?? "").toUpperCase().trim())
                                  }
                                  onClick={() => copyFromFeMutation.mutate(fund)}
                                >
                                  <Copy className="h-3 w-3 mr-1" />
                                  Copy FE
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                Copy CBAM allocation from the FE-driven panel for this fund.
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </td>
                    </tr>
                  );
                })}

              </tbody>
            </table>

          )}
        </CardContent>
      </Card>
    </div>
  );
}

function EditCbamDialog({
  fundName,
  sectors,
  current,
  onSave,
}: {
  fundName: string;
  sectors: { id: string; name: string }[];
  current: { sector_id: string; weighting: number }[];
  onSave: (rows: { sector_id: string; weighting: number }[]) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<{ sector_id: string; value: string }[]>([]);
  const [saving, setSaving] = useState(false);

  const handleOpenChange = (v: boolean) => {
    if (v) {
      setRows(
        current.length > 0
          ? current.map((c) => ({ sector_id: c.sector_id, value: c.weighting.toFixed(2) }))
          : [{ sector_id: "", value: "" }],
      );
    }
    setOpen(v);
  };

  const total = rows.reduce((s, r) => s + (parseFloat(r.value) || 0), 0);
  const ok = Math.abs(total - 100) < 0.5;
  const sectorName = (id: string) => sectors.find((s) => s.id === id)?.name ?? "";
  const usedIds = new Set(rows.map((r) => r.sector_id).filter(Boolean));

  const addRow = () => {
    if (rows.length >= 5) {
      toast.error("Maximum 5 sectors");
      return;
    }
    setRows((prev) => [...prev, { sector_id: "", value: "" }]);
  };

  const save = async () => {
    const out: { sector_id: string; weighting: number }[] = [];
    const seen = new Set<string>();
    for (const r of rows) {
      if (!r.sector_id) continue;
      if (seen.has(r.sector_id)) {
        toast.error("Duplicate sector");
        return;
      }
      const v = parseFloat(r.value);
      if (isNaN(v) || v <= 0) continue;
      seen.add(r.sector_id);
      out.push({ sector_id: r.sector_id, weighting: v });
    }
    if (out.length > 0 && Math.abs(out.reduce((s, r) => s + r.weighting, 0) - 100) > 0.5) {
      toast.error("Weights must total ~100%");
      return;
    }
    setSaving(true);
    try {
      await onSave(out);
      setOpen(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button size="sm" variant="ghost" className="h-6 text-[10px] px-2">
          Edit allocation
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>CBAM allocation — {fundName}</DialogTitle>
          <DialogDescription>
            Add, edit, or remove CBAM master sectors (max 5). Weights must total ~100%.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
          {rows.length === 0 && (
            <div className="text-xs text-muted-foreground italic py-2">
              No sectors yet — add one below.
            </div>
          )}
          {rows.map((r, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <select
                className="flex-1 h-8 text-xs border rounded px-2 bg-background"
                value={r.sector_id}
                onChange={(e) =>
                  setRows((prev) =>
                    prev.map((x, i) => (i === idx ? { ...x, sector_id: e.target.value } : x)),
                  )
                }
              >
                <option value="">Select sector…</option>
                {sectors.map((s) => (
                  <option key={s.id} value={s.id} disabled={usedIds.has(s.id) && s.id !== r.sector_id}>
                    {s.name}
                  </option>
                ))}
              </select>
              <Input
                type="number"
                step="0.01"
                className="w-24 h-8 text-xs"
                value={r.value}
                onChange={(e) =>
                  setRows((prev) =>
                    prev.map((x, i) => (i === idx ? { ...x, value: e.target.value } : x)),
                  )
                }
              />
              <span className="text-[10px] text-muted-foreground">%</span>
              <button
                type="button"
                className="text-muted-foreground hover:text-destructive text-xs px-1"
                title="Remove sector"
                onClick={() => setRows((prev) => prev.filter((_, i) => i !== idx))}
              >
                ✕
              </button>
            </div>
          ))}
          <div className="pt-1">
            <Button size="sm" variant="outline" className="h-8 text-xs w-full" onClick={addRow} disabled={rows.length >= 5}>
              + Add sector
            </Button>
          </div>
        </div>
        <div className="flex items-center justify-between pt-2">
          <Badge variant={ok || total === 0 ? "outline" : "destructive"} className="text-[10px]">
            Total {total.toFixed(1)}%
          </Badge>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={save} disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

